import client from "../../external/open-api";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationError } from "../../models/errors/application-error";
import { ApplicationSupplierOrderSummaryList } from "../../models/supplier/application-order-summary-list";
import { ApplicationSupplierOrderSummary } from "../../models/supplier/application-supplier-order-summary";
import BaseService from "../base-service";
import ISupplierOrderInterface from "./supplier-order-interface";

export default class SupplierOrderService extends BaseService implements ISupplierOrderInterface {
    async get(
        supplierId: number,
        currentPosition: number,
        fetchNext: number,
        fetchAll: boolean,
    ): Promise<ApplicationSupplierOrderSummaryList> {
        const { data, error } = await this.withRetry(() => client.GET(
            "/v{version}/api/supplierorder/{supplierId}",
            {
                params: {
                    path: { version: "1", supplierId },
                    query: {
                        "Paging.CurrentPosition": currentPosition,
                        "Paging.FetchNext": fetchNext,
                        "Paging.FetchAll": fetchAll,
                    },
                },
            }
        ));

        if (!data?.data || !data?.success || error) {
            throw new ApplicationError("Failed to fetch supplier orders");
        }

        return {
            orders: this.mapToSupplierOrderSummaries(data.data.orders ?? []),
            count: data.data.count ?? 0,
            currentPosition: data.data.currentPosition ?? 0,
        };
    }

    async getOrderPDF(supplierId: number, orderId: number): Promise<Blob> {
        const { data, error, response } = await this.withRetry(() => client.GET(
            "/v{version}/api/supplierorder/{supplierId}/order/{orderId}",
            {
                params: {
                    path: { version: "1", supplierId, orderId },
                },
            }
        ));

        if (response?.status === 204) {
            throw new ApplicationError("No order PDF found");
        }

        if (!data?.data?.file || error) {
            throw new ApplicationError("Api call failed fetching order PDF");
        }

        // Safely decode the base64 file content
        const byteCharacters = atob(data.data.file);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: "application/pdf" });
    }

    private mapToSupplierOrderSummaries(data: components["schemas"]["SupplierOrderSummaryDTO"][]): ApplicationSupplierOrderSummary[] {
        return data.map((x) => this.mapToSupplierOrderSummary(x));
    }

    private mapToSupplierOrderSummary(data: components["schemas"]["SupplierOrderSummaryDTO"]): ApplicationSupplierOrderSummary {
        return {
            id: data.orderId ?? 0,
            itemCount: data.itemCount ?? 0,
            createdDate: data.createdDate ? new Date(data.createdDate) : new Date(),
        };
    }
}
